import React, { useState, useEffect, useRef } from 'react'
import CourseCard from '../../components/Course/CourseCard'
import { useTranslation } from 'react-i18next'
import api from '../../api/api'
import TabHeader from '../../components/Header/TabHeader'
import { 
    MdApps, 
    MdSchool, 
    MdTranslate, 
    MdSportsBaseball, 
    MdScience, 
    MdFitnessCenter,
    MdSportsMartialArts,
    MdTheaterComedy,
    MdTrendingUp,
    MdComputer,
    MdChildCare,
    MdMoreHoriz,
    MdKeyboardArrowDown,
    MdLocationCity, 
    MdFlag,
} from 'react-icons/md'
import { FaPalette } from 'react-icons/fa'
import { IoMusicalNotes } from 'react-icons/io5'
import { useProfileStore } from '../../store/useUserStore'
import { isColorDark } from '../../helpers/helper'
import { useNavigate } from 'react-router-dom'

interface Schedule {
    start_time: string
    end_time: string
    day: {
        value: number
        label: string
    }
}

interface CourseType {
    icon: string
    color: string
    name: string
}

interface Course {
    id: number
    course_name: string
    images: string[]
    course_id: string
    branch_id: number
    branch_name: string
    course_branch_id: number
    schedules: Schedule[]
    course_type: CourseType
}

interface APIResponse {
    count: number
    page: number
    page_size: number
    total_pages: number
    result: {
        code: number
        message: string
        data: Course[]
    }
}

interface Category {
    id: number | string
    name: string
    image_icon: string
    color_code: string
    icon?: any
}

interface State {
    id: number
    name: string
    flagImage?: string
}

const Explore = () => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const [searchQuery, setSearchQuery] = useState('')
    const [selectedLocation1, setSelectedLocation1] = useState('')
    const [selectedLocation2, setSelectedLocation2] = useState('')
    const [selectedCategories, setSelectedCategories] = useState<string[]>(['all']) // Default to "All" selected
    const [showCategoryDropdown, setShowCategoryDropdown] = useState(false)
    const [courses, setCourses] = useState<Course[]>([])
    const [categories, setCategories] = useState<Category[]>([])
    const [malaysianStates, setMalaysianStates] = useState<State[]>([])
    const [cities, setCities] = useState<Array<{ id: number; city_name: string; state: number }>>([])
    const dropdownRef = useRef<HTMLDivElement>(null)
    const lastFetchedCategoriesRef = useRef<string[]>(['all']) // Track last fetched categories
    const { setLoading, homePageShow } = useProfileStore()

    // Fetch courses from API
    const fetchCourses = async (categoryIds?: string[], searchTerm?: string, stateId?: number, cityId?: number) => {
        try {
            setLoading(true)
            // Build the URL with category, search, state, and city parameters
            let url = `${process.env.REACT_APP_API_BASE_URL}/parent/explore?page=1`
            
            // Add category parameter if categories are selected and not "all"
            if (categoryIds && categoryIds.length > 0 && !categoryIds.includes('all')) {
                // Convert string IDs back to numbers for API call, excluding 'all'
                const numericIds = categoryIds.filter(id => id !== 'all').join(',')
                if (numericIds) {
                    url += `&category=${numericIds}`
                }
            }

            // Add search parameter if search term is provided
            if (searchTerm && searchTerm.trim()) {
                url += `&search=${encodeURIComponent(searchTerm.trim())}`
            }

            // Add state parameter if state ID is provided
            if (stateId) {
                url += `&state=${stateId}`
            }

            // Add city parameter if city ID is provided
            if (cityId) {
                url += `&city=${cityId}`
            }

            const response = await api.get<APIResponse>(url)
            
            if (response.data.result.code === 200) {
                setCourses(response.data.result.data)
            }
        } catch (err) {
            console.error('Error fetching courses:', err)
        } finally {
            setLoading(false)
        }
    }

    // Fetch categories from API
    const fetchCategories = async () => {
        try {
            const response = await api.get(`${process.env.REACT_APP_API_BASE_URL}/course/type/catagory?page=1&page_size=100`)
            
            if (response.data.code === 200) {
                const transformedData = [
                    { 
                        id: 'all', 
                        name: 'All', 
                        image_icon: '', 
                        color_code: '00D4AA',
                        icon: MdApps
                    },
                    ...response.data.data.map((cat: any) => ({
                        ...cat,
                        id: cat.id.toString(),
                        icon: getCategoryIcon(cat.name)
                    }))
                ]
                setCategories(transformedData)
            }
        } catch (err) {
            console.error('Error fetching categories:', err)
        }
    }

    // Load courses, categories, and states on component mount
    useEffect(() => {
        if (homePageShow && homePageShow === "school") {
            return navigate("/school")
        } else if (homePageShow && homePageShow === "academy") {
            return navigate("/academy")
        }
        
        fetchCourses()
        fetchCategories()
        fetchStates()
    }, [])

    // Transform API course data to CourseCard format
    const transformCourseData = (course: Course) => {
        // Extract days from schedules and convert to abbreviated format
        const dayMap: { [key: number]: string } = {
            1: 'Mon',
            2: 'Tue', 
            3: 'Wed',
            4: 'Thu',
            5: 'Fri',
            6: 'Sat',
            7: 'Sun'
        }

        const concatDayArray = Array.from(new Set(course.schedules.map(schedule => 
            dayMap[schedule.day.value] || schedule.day.label
        )))

        // Create a mock pricing package (since it's not in the API response)
        // You may need to fetch this data separately or include it in the API
        const pricing_packages = [{ id: 1, fee: 80, count: concatDayArray.length }]

        return {
            course_id: course.course_branch_id, // Using course_branch_id as the main ID
            name: course.course_name,
            concatDayArray,
            images: course.images,
            pricing_packages,
            navigateTo: `/explore/course/${course.course_branch_id}`
        }
    }

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                if (showCategoryDropdown) {
                    setShowCategoryDropdown(false)
                    // Only fetch courses if categories have changed
                    const categoriesChanged = JSON.stringify(selectedCategories.sort()) !== JSON.stringify(lastFetchedCategoriesRef.current.sort())
                    if (categoriesChanged) {
                        const selectedState = selectedLocation1 ? malaysianStates.find(state => state.name === selectedLocation1) : null
                        const selectedCity = selectedLocation2 ? cities.find(city => city.city_name === selectedLocation2) : null
                        fetchCourses(selectedCategories, searchQuery, selectedState?.id, selectedCity?.id)
                        lastFetchedCategoriesRef.current = [...selectedCategories]
                    }
                }
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [showCategoryDropdown, selectedCategories])

    // Fetch states from API
    const fetchStates = async () => {
        try {
            const response = await api.get(`${process.env.REACT_APP_API_BASE_URL}/parent/explore/state?page=1&page_size=100`)
            
            if (response.data.result.code === 200) {
                // Transform API data and add flag images and colors
                const transformedStates = response.data.result.data.map((state: any, index: number) => ({
                    id: state.id,
                    name: state.state_name,
                    flagImage: state.image_icon,
                }))
                setMalaysianStates(transformedStates)
                
                // Leave both state and city empty by default
                // User will need to manually select both
            }
        } catch (err) {
            console.error('Error fetching states:', err)
        }
    }


    // Fetch cities from API based on state ID
    const fetchCities = async (stateId: number) => {
        try {
            const response = await api.get(`${process.env.REACT_APP_API_BASE_URL}/parent/explore/state/city?id=${stateId}`)
            
            if (response.data.result.code === 200) {
                setCities(response.data.result.data)
            }
        } catch (err) {
            console.error('Error fetching cities:', err)
            setCities([]) // Reset cities on error
        }
    }


    // Create icon mapping for categories
    const getCategoryIcon = (categoryName: string) => {
        const iconMap: { [key: string]: any } = {
            'All': MdApps,
            'Academics': MdSchool,
            'Academic': MdSchool,
            'Art': FaPalette,
            'Language': MdTranslate,
            'Music': IoMusicalNotes,
            'Sport': MdSportsBaseball,
            'STEM': MdScience,
            'Lifestyle': MdFitnessCenter,
            'Martial Art': MdSportsMartialArts,
            'Dance': MdTheaterComedy,
            'Business': MdTrendingUp,
            'Performing Art': MdTheaterComedy,
            'Digital': MdComputer,
            'Kindergarten': MdChildCare,
            'Other': MdMoreHoriz
        }
        return iconMap[categoryName] || MdMoreHoriz
    }

    const toggleCategory = (categoryId: string) => {
        setSelectedCategories(prev => {
            if (categoryId === 'all') {
                // If "All" is clicked, select only "All" and deselect everything else
                return ['all']
            } else {
                // If any other category is clicked
                if (prev.includes(categoryId)) {
                    // If category is already selected, remove it
                    const newSelection = prev.filter(id => id !== categoryId)
                    // If no categories left, default back to "All"
                    return newSelection.length === 0 ? ['all'] : newSelection
                } else {
                    // If category is not selected, add it and remove "All"
                    const newSelection = prev.filter(id => id !== 'all')
                    return [...newSelection, categoryId]
                }
            }
        })
    }

    // Handle dropdown toggle
    const handleDropdownToggle = () => {
        if (showCategoryDropdown) {
            // Closing dropdown - only fetch courses if categories have changed
            setShowCategoryDropdown(false)
            const categoriesChanged = JSON.stringify(selectedCategories.sort()) !== JSON.stringify(lastFetchedCategoriesRef.current.sort())
            if (categoriesChanged) {
                const selectedState = selectedLocation1 ? malaysianStates.find(state => state.name === selectedLocation1) : null
                const selectedCity = selectedLocation2 ? cities.find(city => city.city_name === selectedLocation2) : null
                fetchCourses(selectedCategories, searchQuery, selectedState?.id, selectedCity?.id)
                lastFetchedCategoriesRef.current = [...selectedCategories]
            }
        } else {
            // Opening dropdown
            setShowCategoryDropdown(true)
        }
    }

    // Handle state change and update city accordingly
    const handleStateChange = (stateName: string) => {
        setSelectedLocation1(stateName)
        // Reset city to empty when state changes
        setSelectedLocation2('')
        
        // Call fetchCourses with selected state if a state is selected
        if (stateName) {
            const selectedState = malaysianStates.find(state => state.name === stateName)
            if (selectedState) {
                // Fetch cities for the selected state
                fetchCities(selectedState.id)
                fetchCourses(selectedCategories, searchQuery, selectedState.id, undefined)
            }
        } else {
            // If no state selected, fetch courses without state filter and clear cities
            setCities([])
            fetchCourses(selectedCategories, searchQuery, undefined, undefined)
        }
    }

    // Handle city change and call fetchCourses
    const handleCityChange = (cityName: string) => {
        setSelectedLocation2(cityName)
        
        // Call fetchCourses with selected city if a city is selected
        if (cityName) {
            const selectedState = selectedLocation1 ? malaysianStates.find(state => state.name === selectedLocation1) : null
            const selectedCity = cities.find(city => city.city_name === cityName)
            fetchCourses(selectedCategories, searchQuery, selectedState?.id, selectedCity?.id)
        } else {
            // If no city selected, fetch courses without city filter
            const selectedState = selectedLocation1 ? malaysianStates.find(state => state.name === selectedLocation1) : null
            fetchCourses(selectedCategories, searchQuery, selectedState?.id, undefined)
        }
    }

    // Handle search functionality
    const handleSearch = () => {
        const selectedState = selectedLocation1 ? malaysianStates.find(state => state.name === selectedLocation1) : null
        const selectedCity = selectedLocation2 ? cities.find(city => city.city_name === selectedLocation2) : null
        fetchCourses(selectedCategories, searchQuery, selectedState?.id, selectedCity?.id)
    }

    // Handle search on Enter key press
    const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            handleSearch()
        }
    }

    return (
        <>
        <TabHeader defaultTab="explore" />
        <div className="px-2 py-4 mb-10">
            {/* Search Bar */}
            <div className="flex items-center mb-6">
                <input 
                    value={searchQuery} 
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={handleSearchKeyPress}
                    className="basis-11/12 bg-gray-200 placeholder:text-slate-400 text-slate-700 text-md border border-slate-200 rounded-l-xl pl-3 py-1 transition duration-300 ease focus:outline-none focus:border-slate-400 hover:border-slate-300 focus:shadow" 
                    placeholder={t('explore.search_courses')} 
                />
                <button 
                    onClick={handleSearch}
                    className="basis-1/12 bg-theme p-2 flex justify-center items-center rounded-r-xl hover:bg-opacity-90 transition-colors"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#FFF" className="w-5 h-5">
                        <path fillRule="evenodd" d="M10.5 3.75a6.75 6.75 0 1 0 0 13.5 6.75 6.75 0 0 0 0-13.5ZM2.25 10.5a8.25 8.25 0 1 1 14.59 5.28l4.69 4.69a.75.75 0 1 1-1.06 1.06l-4.69-4.69A8.25 8.25 0 0 1 2.25 10.5Z" clipRule="evenodd" />
                    </svg>
                </button>
            </div>

            {/* Category Filter */}
            <div className="mb-6 relative" ref={dropdownRef}>
                <div 
                    className="flex items-center justify-between bg-white rounded-lg border border-gray-200 px-4 py-3 cursor-pointer"
                    onClick={handleDropdownToggle}
                >
                    <div className="flex items-center gap-2">
                        <span className="text-gray-700 font-medium mr-2">{t("explore.category")}</span>
                        <div className="flex gap-2">
                            {selectedCategories.slice(0, 4).map((categoryId) => {
                                const category = categories.find(c => c.id === categoryId)
                                if (!category) return null
                                const IconComponent = category.icon
                                return (
                                    <div
                                        key={category.id}
                                        className={`w-6 h-6 rounded-full flex self-center items-center justify-center text-lg transition-all ring-2 ring-offset-2 ring-gray-400`}
                                        style={{ 
                                            backgroundColor: `#${category.color_code}`,
                                            color: category.color_code && isColorDark(category.color_code) ? 'white' : 'black'
                                        }}
                                    >
                                        <IconComponent size={14} />
                                    </div>
                                )
                            })}
                            {selectedCategories.length > 4 && (
                                <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm bg-gray-200 text-gray-600">
                                    +{selectedCategories.length - 4}
                                </div>
                            )}
                        </div>
                    </div>
                    <MdKeyboardArrowDown 
                        className={`w-5 h-5 text-gray-400 transition-transform ${showCategoryDropdown ? 'rotate-180' : ''}`} 
                    />
                </div>

                {/* Category Dropdown */}
                {showCategoryDropdown && (
                    <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg border border-gray-200 shadow-lg z-10 p-4">
                        <div className="grid grid-cols-3 gap-3">
                            {categories.map((category) => {
                                const IconComponent = category.icon
                                const categoryIdStr = category.id.toString()
                                const isSelected = selectedCategories.includes(categoryIdStr)
                                return (
                                    <button
                                        key={category.id}
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            toggleCategory(categoryIdStr)
                                        }}
                                        className={`flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium transition-all border ${
                                            isSelected 
                                                ? `border-transparent text-white`
                                                : 'bg-gray-100 text-gray-800 border-gray-200 hover:bg-gray-200'
                                        }`}
                                        style={isSelected ? {
                                            backgroundColor: `#${category.color_code}`,
                                            color: category.color_code && isColorDark(category.color_code) ? 'white' : 'black'
                                        } : {}}
                                    >
                                        <IconComponent size={14} className="flex-shrink-0" />
                                        <span className="truncate">{t("explore."+category.name)}</span>
                                    </button>
                                )
                            })}
                        </div>
                    </div>
                )}
            </div>

            {/* Location Dropdowns */}
            <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-3">{t("explore.course")}</h3>
                <div className="flex gap-4">
                    {/* First Location Dropdown - Malaysian States */}
                    <div className="flex-1">
                        <div className="relative">
                            <select
                                value={selectedLocation1}
                                onChange={(e) => handleStateChange(e.target.value)}
                                className="w-full bg-white border border-gray-200 rounded-lg px-12 py-3 pr-8 appearance-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                                <option value="">{t("explore.select_state")}</option>
                                {malaysianStates.map((state) => (
                                    <option key={state.name} value={state.name}>
                                        {state.name}
                                    </option>
                                ))}
                            </select>
                            <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                                <MdKeyboardArrowDown className="w-5 h-5 text-gray-400" />
                            </div>
                            <div className="absolute left-3 top-3 w-6 h-6 bg-white-500 rounded-full flex items-center justify-center overflow-hidden border border-gray-300">
                                {
                                    selectedLocation1 && malaysianStates.find(s => s.name === selectedLocation1)?.flagImage
                                        ? <img 
                                            src={malaysianStates.find(s => s.name === selectedLocation1)?.flagImage} 
                                            alt={selectedLocation1 ? `${selectedLocation1} flag` : 'Malaysia flag'}
                                            className="w-full h-full object-cover"
                                            onError={(e) => {
                                                // Fallback to Malaysian flag if state flag is not found
                                                e.currentTarget.src = '/assets/img/flags/malaysia.png'
                                            }}
                                        /> 
                                        : <MdFlag className="w-full h-full text-theme object-cover" />
                                }
                                
                            </div>
                        </div>
                    </div>

                    {/* Second Location Dropdown - Cities */}
                    <div className="flex-1">
                        <div className="relative">
                            <select
                                value={selectedLocation2}
                                onChange={(e) => handleCityChange(e.target.value)}
                                className="w-full bg-white border border-gray-200 rounded-lg px-12 py-3 pr-8 appearance-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                                <option value="">{t("explore.select_city")}</option>
                                {cities.map((city) => (
                                    <option key={city.id} value={city.city_name}>
                                        {city.city_name}
                                    </option>
                                ))}
                            </select>
                            <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                                <MdKeyboardArrowDown className="w-5 h-5 text-gray-400" />
                            </div>
                            <div className="absolute left-3 top-3 w-6 h-6 bg-white-500 rounded-full flex items-center justify-center overflow-hidden border border-gray-300">
                                <MdLocationCity className="w-full h-full text-theme object-cover" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Course Grid */}
            <div className="grid grid-cols-2 gap-4">
                {courses.length === 0 ? (
                    // No courses found
                    <div className="col-span-2 flex justify-center items-center py-8">
                        <div className="text-gray-500">No courses found</div>
                    </div>
                ) : (
                    // Render courses
                    courses.map((course) => {
                        const transformedCourse = transformCourseData(course)
                        return (
                            <CourseCard
                                explore={true}
                                key={transformedCourse.course_id}
                                course_id={transformedCourse.course_id}
                                name={transformedCourse.name}
                                concatDayArray={transformedCourse.concatDayArray}
                                images={transformedCourse.images}
                                pricing_packages={transformedCourse.pricing_packages}
                                navigateTo={transformedCourse.navigateTo}
                            />
                        )
                    })
                )}
            </div>
        </div>
        </>
    )
}

export default Explore